import CloseIcon from '@mui/icons-material/Close';
import {Alert, CircularProgress, Dialog, DialogContent, Fab, Stack} from '@mui/material';
import {useEffect, useState} from 'react';

type CadViewerDialogProps = {
    open: boolean;
    onClose: () => void;
    dxfUrl: string;
};

const CadViewerDialog = ({open, onClose, dxfUrl}: CadViewerDialogProps): JSX.Element => {
    // A callback ref rather than useRef: MUI portals the dialog body in its own layout effect, so
    // on the render where `open` flips true the canvas host does not exist yet. Storing the node in
    // state re-runs the effect once it actually attaches.
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');

    useEffect(() => {
        if (!(open && container)) {
            return;
        }

        let cancelled = false;
        setStatus('loading');

        // Imported here rather than at module scope so the viewer and its three.js dependency stay
        // out of the initial bundle; nothing downloads until a customer asks to see a drawing.
        void import('./cad-viewer')
            .then(async ({openDrawing}) => {
                await openDrawing(container, dxfUrl);

                if (!cancelled) {
                    setStatus('ready');
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setStatus('failed');
                }
            });

        return () => {
            cancelled = true;
        };
    }, [open, container, dxfUrl]);

    return (
        // keepMounted because the viewer is a singleton bound to the canvas host it was first
        // given. Letting MUI unmount the dialog body would hand it a fresh node on every reopen,
        // leaving it drawing into a detached one. It also makes reopening instant.
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            keepMounted
            aria-label="CAD drawing"
        >
            {/* No title bar and no dividers: the drawing carries its own title block, and either
                one shows as a pale seam against the viewer's black canvas. */}
            <DialogContent
                sx={{height: '70vh', p: 0, position: 'relative', bgcolor: 'common.white'}}
            >
                <div ref={setContainer} style={{position: 'absolute', inset: 0}} />
                <Fab
                    size="small"
                    onClick={onClose}
                    aria-label="Close drawing"
                    sx={{position: 'absolute', top: 16, right: 16}}
                >
                    <CloseIcon />
                </Fab>
                {status === 'loading' && (
                    <Stack
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Stack>
                )}
                {status === 'failed' && (
                    <Alert severity="error" sx={{m: 2}}>
                        This drawing could not be displayed. You can still download the CAD file.
                    </Alert>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CadViewerDialog;
